import React, { useState } from 'react';
import { sanitizedInnerHtml } from 'src/utils/sanitize';
import { styled } from '@mui/material/styles';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import styles from '../form-builder/CreateQuestionnaire/components/Preview/Preview.module.css';
import { FORM_TYPES } from 'src/components/form-builder/CreateQuestionnaire/constants/formTypes';
import SignatureCanvas from 'react-signature-canvas';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@mui/material';
import {
  AddCircleRounded,
  BackspaceRounded,
  CloudUploadOutlined,
  Close
} from '@mui/icons-material';
import { FieldArray } from 'formik';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import EmbedContentModal from '../form-builder/CreateQuestionnaire/components/Popup/types/formTypes/EmbedContent/EmbedContentModal';

export const LinketScale = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '15px'
});

export const Label = styled('div')({
  fontSize: '1rem',
  color: 'black',
  fontWeight: 'bold',
  paddingBlock: '10px'
});

const SignatureField = ({ element, formik, fieldKey, t, readOnly }) => {
  const sigRef = React.useRef(null);

  const handleEnd = () => {
    if (sigRef.current) {
      const dataURL = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
      formik.setFieldValue(fieldKey, dataURL);
    }
  };

  const handleClear = () => {
    if (sigRef.current) {
      sigRef.current.clear();
    }
    formik.setFieldValue(fieldKey, '');
  };

  return (
    <div style={{ marginBlock: '10px' }}>
      <FormLabel>
        <Label>
          {t(element.label)}
          {element.isRequired && (
            <span style={{ color: 'red', padding: 5 }}>*</span>
          )}
        </Label>
      </FormLabel>
      {readOnly ? (
        formik.values[fieldKey] ? (
          <img
            src={formik.values[fieldKey]}
            alt="signature"
            style={{ border: '1px solid #ccc', maxWidth: '100%' }}
          />
        ) : (
          <span
            style={{
              padding: '.6rem',
              borderRadius: 5,
              backgroundColor: 'lavender'
            }}
          >
            No signature
          </span>
        )
      ) : (
        <>
          <SignatureCanvas
            ref={sigRef}
            onEnd={handleEnd}
            canvasProps={{
              style: { border: '1px solid #ccc', width: '100%', height: 150 }
            }}
          />
          <Button size="small" onClick={handleClear}>
            Clear
          </Button>
        </>
      )}
      <FormHelperText>
        {formik.touched[fieldKey] && formik.errors[fieldKey]}
      </FormHelperText>
    </div>
  );
};

const FileUploadField = ({ element, formik, fieldKey, t, readOnly }) => {
  const inputRef = React.useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const value = formik.values[fieldKey];
  const hasError = formik.touched[fieldKey] && Boolean(formik.errors[fieldKey]);

  const setFile = (file) => {
    if (file) formik.setFieldValue(fieldKey, file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (readOnly) return;
    setFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  return (
    <div style={{ marginBlock: '10px' }}>
      <FormLabel>
        <Label>
          {t(element.label)}
          {element.isRequired && (
            <span style={{ color: 'red', padding: 5 }}>*</span>
          )}
        </Label>
      </FormLabel>
      {readOnly ? (
        value && value.type ? (
          // `url` is signed by the server for this view; `key` is the file's
          // storage name, not a link (formBuilderController.signFileAnswers).
          <a target="_blank" rel="noopener noreferrer" href={value.url}>
            <Button variant="contained" color="secondary" component="label">
              View
            </Button>
          </a>
        ) : (
          <span
            style={{
              padding: '.6rem',
              borderRadius: 5,
              backgroundColor: 'lavender'
            }}
          >
            Not uploaded
          </span>
        )
      ) : (
        <div
          className={`${styles.dropzone} ${
            dragActive ? styles.dropzoneActive : ''
          }`}
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
        >
          <CloudUploadOutlined className={styles.dropzoneIcon} />
          {value && value.name ? (
            <span
              className={styles.dropzoneText}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {value.name}
              <IconButton
                size="small"
                aria-label="remove file"
                onClick={(e) => {
                  e.stopPropagation();
                  formik.setFieldValue(fieldKey, '');
                  if (inputRef.current) inputRef.current.value = '';
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </span>
          ) : (
            <>
              <span className={styles.dropzoneText}>
                {t('Drag & drop a file here')}
              </span>
              <span className={styles.dropzoneHint}>
                {t('PDF, image or text file')}
              </span>
            </>
          )}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            className={styles.dropzoneBtn}
            onClick={(e) => {
              e.stopPropagation();
              if (inputRef.current) inputRef.current.click();
            }}
          >
            {value && value.name ? t('Replace File') : t('Upload File')}
          </Button>
          <input
            ref={inputRef}
            onChange={(e) => setFile(e.target.files[0])}
            accept="image/*,.pdf,.txt"
            hidden
            type="file"
          />
        </div>
      )}
      <FormHelperText>
        {hasError && (
          <span style={{ color: 'red' }}>Above field is required</span>
        )}
      </FormHelperText>
    </div>
  );
};

const FormFields = ({ element, formik, readOnly }) => {
  const fontColor = {
    style: { color: 'rgb(50, 50, 50)' }
  };

  //encoding label and replacing period  to resolve conflicting with formik
  let key = encodeURIComponent(element.label.replaceAll('.', '_'));

  const t = useAutoTranslation();
  const [openModal, setOpenModal] = useState(false);

  switch (element.type) {
    case FORM_TYPES.SHORT_TEXT:
      return (
        <div style={{ marginBlock: '10px' }}>
          <FormLabel>
            <Label>
              {t(element.label)}
              {element.isRequired && (
                <span style={{ color: 'red', padding: 5 }}>*</span>
              )}
            </Label>
          </FormLabel>
          <FieldArray
            name={key}
            render={(arrayHelpers) => (
              <div>
                <div>
                  {formik.values[key] &&
                    formik.values[key].map((val, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          gap: '1rem',
                          marginBottom: 10,
                          alignItems: 'center'
                        }}
                      >
                        <TextField
                          disabled={readOnly}
                          name={`${[key] + `[${index}]`}`}
                          value={formik.values[key][index]}
                          onChange={formik.handleChange}
                          error={
                            formik.touched[key] && Boolean(formik.errors[key])
                          }
                          variant="outlined"
                          fullWidth
                          slotProps={{
                            htmlInput: fontColor
                          }}
                        />
                        {!readOnly && !!index && (
                          <BackspaceRounded
                            style={{ fontSize: 'medium', color: '#ff4d4d' }}
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            Remove
                          </BackspaceRounded>
                        )}
                      </div>
                    ))}
                  <FormHelperText>
                    {formik.touched[key] &&
                      Boolean(formik.errors[key]) &&
                      'Above fields are required'}
                  </FormHelperText>
                  {element.isIdentifier && !readOnly && (
                    <div
                      onClick={() => {
                        if (
                          formik.values[key][formik.values[key].length - 1] !==
                          ''
                        )
                          arrayHelpers.push('');
                      }}
                      style={{
                        display: 'flex',
                        paddingBlock: 10,
                        alignItems: 'center'
                      }}
                    >
                      <AddCircleRounded
                        style={{ fontSize: 'large', color: 'green' }}
                      />{' '}
                      <TextField
                        disabled
                        variant="outlined"
                        placeholder="add more"
                        fullWidth
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          />
        </div>
      );
    case FORM_TYPES.EMAIL_INPUT:
      return (
        <div style={{ marginBlock: '10px' }}>
          <FormLabel>
            <Label>
              {t(element.label)}
              {element.isRequired && (
                <span style={{ color: 'red', padding: 5 }}>*</span>
              )}
            </Label>
          </FormLabel>
          <TextField
            disabled={readOnly}
            variant="outlined"
            value={formik.values[key] ? formik.values[key] : ''}
            onChange={formik.handleChange}
            name={key}
            type={'email'}
            onBlur={formik.handleBlur}
            error={formik.touched[key] && Boolean(formik.errors[key])}
            helperText={formik.touched[key] && formik.errors[key]}
            fullWidth
            slotProps={{
              htmlInput: fontColor
            }}
          />
        </div>
      );
    case FORM_TYPES.FILE_UPLOAD:
      return (
        <FileUploadField
          element={element}
          formik={formik}
          fieldKey={key}
          t={t}
          readOnly={readOnly}
        />
      );
    case FORM_TYPES.DATE_TIME_INPUT:
      return (
        <div style={{ marginBlock: '10px' }}>
          <FormLabel>
            <Label>
              {t(element.label)}
              {element.isRequired && (
                <span style={{ color: 'red', padding: 5 }}>*</span>
              )}
            </Label>
          </FormLabel>
          <TextField
            disabled={readOnly}
            variant="outlined"
            value={formik.values[key] ? formik.values[key] : ''}
            onChange={formik.handleChange}
            name={key}
            type={'date'}
            onBlur={formik.handleBlur}
            error={formik.touched[key] && Boolean(formik.errors[key])}
            helperText={formik.touched[key] && formik.errors[key]}
            fullWidth
            slotProps={{
              htmlInput: fontColor
            }}
          />
        </div>
      );

    case FORM_TYPES.LONG_TEXT:
      return (
        <div style={{ marginBlock: '10px', paddingBlock: '10px' }}>
          <FormLabel>
            <Label>
              {t(element.label)}
              {element.isRequired && (
                <span style={{ color: 'red', padding: 5 }}>*</span>
              )}
            </Label>
          </FormLabel>
          <TextField
            disabled={readOnly}
            multiline
            value={formik.values[key] ? formik.values[key] : ''}
            onChange={formik.handleChange}
            id={key}
            onBlur={formik.handleBlur}
            error={formik.touched[key] && Boolean(formik.errors[key])}
            helperText={formik.touched[key] && formik.errors[key]}
            minRows={6}
            variant="outlined"
            fullWidth
            slotProps={{
              htmlInput: fontColor
            }}
          />
        </div>
      );
    case FORM_TYPES.MULTIPLE_CHOICE:
      return (
        <div
          style={{
            paddingBlock: '10px',
            marginBlock: '10px'
          }}
        >
          <FormControl
            error={formik.touched[key] && Boolean(formik.errors[key])}
            onBlur={formik.handleBlur}
            disabled={readOnly}
          >
            <FormLabel>
              <Label>
                {t(element.label)}
                {element.isRequired && (
                  <span style={{ color: 'red', padding: 5 }}>*</span>
                )}
              </Label>
            </FormLabel>

            <RadioGroup
              name={key}
              value={formik.values[key] ? formik.values[key] : ''}
              onChange={formik.handleChange}
            >
              {element.options.map((option, index) => {
                return (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={<Radio />}
                    label={option}
                    inputprops={fontColor}
                  />
                );
              })}
            </RadioGroup>

            <FormHelperText>
              {formik.touched[key] && formik.errors[key]}
            </FormHelperText>
          </FormControl>
        </div>
      );
    case FORM_TYPES.DROPDOWN:
      return (
        <div style={{ marginBlock: '10px' }}>
          <FormControl
            style={{ width: '100%' }}
            error={formik.touched[key] && Boolean(formik.errors[key])}
            name={key}
            disabled={readOnly}
          >
            <FormLabel>
              <Label>
                {t(element.label)}
                {element.isRequired && (
                  <span style={{ color: 'red', padding: 5 }}>*</span>
                )}
              </Label>
            </FormLabel>
            <Select
              onBlur={formik.handleBlur}
              value={formik.values[key] ? formik.values[key] : ''}
              name={key}
              // autoWidth={true}
              style={{ paddingRight: 15 }}
              variant="outlined"
              onChange={formik.handleChange}
              // label={key}
              inputProps={fontColor}
            >
              {element.options.map((option) => {
                return (
                  <MenuItem name={key} key={option} value={option}>
                    {option}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText>
              {formik.touched[key] && formik.errors[key]}
            </FormHelperText>
          </FormControl>
        </div>
      );
    case FORM_TYPES.CHECKBOX_INPUT:
      return (
        <div
          style={{
            paddingBlock: '10px',
            marginBlock: '15px'
          }}
        >
          <FormControl
            error={formik.touched[key] && Boolean(formik.errors[key])}
            onBlur={formik.handleBlur}
            disabled={readOnly}
            style={{ width: '100%' }}
          >
            <FormLabel>
              <Label>
                {t(element.label)}
                {element.isRequired && (
                  <span style={{ color: 'red', padding: 5 }}>*</span>
                )}
              </Label>
            </FormLabel>
            <FieldArray
              name={key}
              render={(arrayHelpers) => (
                <div>
                  <div>
                    {formik.values[key] &&
                      formik.values[key].map((val, index) => {
                        return (
                          <div key={index}>
                            <div
                              style={{
                                display: 'flex',
                                gap: '1rem',
                                marginBottom: 10,
                                alignItems: 'center'
                              }}
                            >
                              <Checkbox
                                disabled={readOnly}
                                name={`${[key] + `[${index}].checked`}`}
                                onChange={formik.handleChange}
                                checked={formik.values[key][index].checked}
                              />
                              <span
                                disabled={readOnly}
                                name={`${[key] + `[${index}].label`}`}
                              >
                                {formik.values[key][index].label}
                              </span>
                            </div>
                            {element.isIdentifier &&
                              formik.values[key][index].label === 'Other' &&
                              formik.values[key][index].checked && (
                                <TextField
                                  disabled={readOnly}
                                  name={`${[key] + `[${index}].value`}`}
                                  value={formik.values[key][index].value}
                                  onChange={formik.handleChange}
                                  variant="outlined"
                                  placeholder="Specify"
                                  fullWidth
                                />
                              )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            />
            <FormHelperText>
              {formik.touched[key] && formik.errors[key]}
            </FormHelperText>
          </FormControl>
        </div>
      );
    case FORM_TYPES.LINEAR_SCALE:
      return (
        <div
          style={{
            paddingBlock: '10px',
            marginBlock: '15px'
          }}
        >
          <FormControl disabled={readOnly}>
            <FormLabel
              error={formik.touched[key] && Boolean(formik.errors[key])}
              onBlur={formik.handleBlur}
            >
              <Label>
                {t(element.label)}
                {element.isRequired && (
                  <span style={{ color: 'red', padding: 5 }}>*</span>
                )}
              </Label>
            </FormLabel>
            <RadioGroup
              row
              name={key}
              value={formik.values[key] ? formik.values[key] : ''}
              onChange={formik.handleChange}
            >
              {element.options.map((option, index) => {
                return (
                  <LinketScale key={index}>
                    <span>{option}</span>
                    <FormControlLabel
                      style={{ marginLeft: '10px' }}
                      value={option}
                      control={<Radio />}
                    />
                  </LinketScale>
                );
              })}
            </RadioGroup>
            <FormHelperText style={{ color: 'red' }}>
              {formik.touched[key] && formik.errors[key]}
            </FormHelperText>
          </FormControl>
        </div>
      );
    case FORM_TYPES.EMBED_CONTENT:
      return (
        <div
          style={{
            marginBlock: '10px'
          }}
        >
          <FormLabel>
            <EmbedContentModal
              open={openModal}
              setOpen={setOpenModal}
              text={t(element.value)}
              title={t(element.label)}
            />

            <div
              className={
                element.hasModalContent
                  ? styles.clickBlock
                  : styles.previewLabel
              }
              onClick={() => element.hasModalContent && setOpenModal(true)}
            >
              <span>{t(element.label)}</span>
              {element.hasModalContent && (
                <div className={styles.viewMoreSection}>
                  <AdsClickIcon fontSize="10px" />
                  <p className={styles.moreDetails}>
                    Click to view more details
                  </p>
                </div>
              )}
            </div>
          </FormLabel>
          {!element.hasModalContent && (
            <div>
              <div
                dangerouslySetInnerHTML={sanitizedInnerHtml(t(element.value))}
              />
            </div>
          )}
        </div>
      );

    case FORM_TYPES.SIGNATURE:
      return (
        <SignatureField
          element={element}
          formik={formik}
          fieldKey={key}
          t={t}
          readOnly={readOnly}
        />
      );
    default:
      return <></>;
  }
};

export default FormFields;
